const sectionStyle = {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "60px 20px"
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "20px"
};

const cardStyle = {
  border: "1px solid #eee",
  borderRadius: "12px",
  padding: "20px",
  background: "#fff",
  boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease"
};

const hoverEffect = (e, enter = true) => {
  e.currentTarget.style.boxShadow = enter
    ? "0 15px 35px rgba(0,0,0,0.1)"
    : "0 10px 25px rgba(0,0,0,0.05)";
  e.currentTarget.style.transform = enter
    ? "translateY(-3px)"
    : "translateY(0)";
};

const AwsDatabaseSpecialty = () => {
  return (
    <div>

      {/* HERO */}
      <div style={{ ...sectionStyle, paddingTop: "80px" }}>
        <h1 style={{ fontSize: "36px", marginBottom: "20px" }}>
          Designing Scalable Data Layers on AWS
        </h1>

        <p style={{ fontSize: "18px", color: "#555", maxWidth: "700px" }}>
          With the AWS Certified Database – Specialty certification, I design and optimize
          database architectures tailored to workload requirements, performance goals, and scalability needs.
        </p>
      </div>


      {/* IMAGE */}
      <div style={{ ...sectionStyle, paddingTop: "0", textAlign: "center" }}>
        <img
          src="/images/AwsDatabaseBadge.png"
          alt="AWS Certified Database Specialty"
          style={{
            width: "100%",
            maxWidth: "280px",
            borderRadius: "12px",
            boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
          }}
        />

        <div style={{ fontSize: "14px", color: "#777", marginTop: "10px" }}>
          AWS Certified Database – Specialty
        </div>
      </div>


      {/* INTRO */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          This certification reflects deep expertise in selecting, designing, and managing
          data storage solutions across a wide range of AWS database technologies.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          The key is not just using databases — but choosing the right one for each use case
          and integrating it properly into a scalable architecture.
        </p>
      </div>


      {/* CAPABILITIES 🔥 */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          Database Capabilities I Can Deliver
        </h2>

        <div style={gridStyle}>
          {[
            "Selecting the right database technology based on workload characteristics",
            "Designing relational systems using Amazon RDS and Aurora",
            "Building scalable NoSQL architectures with DynamoDB",
            "Designing efficient data models for performance and scalability",
            "Optimizing queries, indexing strategies, and access patterns",
            "Designing multi-tier and distributed data architectures",
            "Ensuring high availability and fault tolerance in data layers",
            "Optimizing database cost and resource utilization",
            "Integrating databases into event-driven and serverless systems"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              ✓ {item}
            </div>
          ))}
        </div>
      </div>


      {/* DATABASE TYPES 🔥 */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "25px" }}>
          Working Across Database Types
        </h2>

        <div style={gridStyle}>
          {[
            "Relational databases (RDS, Aurora) for structured transactional systems",
            "NoSQL databases (DynamoDB) for scalable, high-throughput workloads",
            "Data lakes (S3-based architectures) for analytics and large datasets",
            "Caching layers (ElastiCache) for performance optimization",
            "Search solutions (OpenSearch) for advanced querying",
            "Hybrid architectures combining multiple database types"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* USE CASES 🔥 */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          Where This Creates Real Value
        </h2>

        <div style={gridStyle}>
          {[
            "Designing scalable backend systems with efficient data access",
            "Optimizing existing databases for performance and cost",
            "Migrating legacy databases to AWS environments",
            "Building data layers for high-traffic applications",
            "Supporting analytics and reporting platforms",
            "Designing data architectures for AI and machine learning systems"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* POSITIONING */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          Database design is one of the most critical decisions in system architecture —
          it directly affects performance, scalability, and long-term maintainability.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          I approach data architecture holistically, ensuring that the chosen database solutions
          align with application patterns and business requirements.
        </p>
      </div>


      {/* CTA 🔥 */}
      <div style={{
        textAlign: "center",
        margin: "80px 0",
        padding: "50px 20px",
        background: "#fafafa"
      }}>
        <h2 style={{ marginBottom: "15px" }}>
          Design the Right Data Architecture for Your System
        </h2>

        <p style={{ color: "#555", marginBottom: "25px" }}>
          Let’s choose and implement the optimal database strategy for your application.
        </p>

        <a
          href="/contact"
          style={{
            display: "inline-block",
            padding: "14px 32px",
            background: "#007bff",
            color: "#fff",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: "500",
            boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
          }}
        >
          Discuss Your Data Architecture →
        </a>
      </div>

    </div>
  );
};

export default AwsDatabaseSpecialty;